import queryClient from '@src/app/providers/queryClient';
import type {
  Task,
  TaskComplete,
  TaskCompleteDto,
  TaskCreate,
  TaskCreateDto,
  TaskDto,
  TaskPrioritize,
  TaskPrioritizeDto,
  TaskPriorityChoicesResponse,
  TaskReassign,
  TaskReassignDto,
  TaskStatusChoicesResponse,
  TaskSummaryResponse,
  TasksResponse,
  TaskTypeChoice,
  TaskTypeChoicesResponseDto,
  TaskUpdate,
  TaskUpdateDto,
} from '@src/features/tasks';
import {
  taskCreateToDto,
  taskFromDto,
  taskTypeChoiceFromDto,
  taskUpdateToDto,
} from '@src/features/tasks/lib/task';
import useAPIClient from '@src/shared/hooks/api/useAPIClient';
import { useMutation, useQuery } from '@src/shared/query';
import type { VisibleListState } from '@src/shared/realtime';
import { createRealtimeInfiniteListCache } from '@src/shared/realtime';
import {
  type InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
} from '@tanstack/react-query';

import { tasksUrl } from './constants';

export type UseTasksParams = {
  filterParams?: Record<string, unknown>;
  pageSize: number;
  ordering?: string;
  retainedTaskIds?: number[];
  preservedTaskIds?: number[];
};

export type TasksListResponse = Omit<TasksResponse, 'results'> & {
  results: Task[];
};

export type UpdateTaskPayload = {
  id: number | string;
  payload: Partial<TaskUpdate>;
};

type TaskListQueryKeyParams = Pick<
  UseTasksParams,
  'filterParams' | 'ordering' | 'pageSize'
>;
type TaskListQueryKey = [
  'root',
  'tasks',
  number,
  string | undefined,
  Record<string, unknown>,
];

function getTaskListQueryKey({
  filterParams = {},
  ordering,
  pageSize,
}: TaskListQueryKeyParams): TaskListQueryKey {
  return ['root', 'tasks', pageSize, ordering, filterParams];
}

const taskListCache = createRealtimeInfiniteListCache<
  TaskDto,
  TasksResponse,
  TaskListQueryKeyParams
>({
  queryClient,
  queryRoot: ['root', 'tasks'],
  getQueryKey: getTaskListQueryKey,
});

export function updateTaskInCachedLists(updatedTaskDto: TaskDto) {
  taskListCache.updateOnePreservingPosition(updatedTaskDto);
}

export function markTaskListsStale() {
  return queryClient.invalidateQueries({
    predicate: (query) =>
      query.queryKey[0] === 'root' &&
      query.queryKey[1] === 'tasks' &&
      typeof query.queryKey[2] === 'number',
    refetchType: 'none',
  });
}

function resyncActiveTaskLists() {
  return queryClient.invalidateQueries({
    predicate: (query) =>
      query.queryKey[0] === 'root' &&
      query.queryKey[1] === 'tasks' &&
      typeof query.queryKey[2] === 'number',
    refetchType: 'active',
  });
}

export function prependTaskToCachedList(
  params: TaskListQueryKeyParams,
  createdTaskDto: TaskDto,
) {
  return taskListCache.prependNew(params, createdTaskDto);
}

export function mergeTaskVisibleListState(
  params: TaskListQueryKeyParams,
  state: VisibleListState<TaskDto>,
) {
  return taskListCache.mergeVisibleState(params, state);
}

function invalidateTaskSummary() {
  return queryClient.invalidateQueries({
    queryKey: ['root', 'tasks', 'summary'],
  });
}

function handleTaskMutationError() {
  void resyncActiveTaskLists();
  void invalidateTaskSummary();
}

export function useTasks({
  filterParams = {},
  pageSize,
  ordering,
  retainedTaskIds = [],
  preservedTaskIds = [],
}: UseTasksParams) {
  const apiClient = useAPIClient();

  return useInfiniteQuery({
    queryKey: getTaskListQueryKey({ filterParams, ordering, pageSize }),
    initialPageParam: 1,
    placeholderData: keepPreviousData,
    queryFn: ({ pageParam, signal }) =>
      apiClient.request({
        url: tasksUrl,
        payload: {
          page: String(pageParam),
          page_size: String(pageSize),
          ...filterParams,
          ...(retainedTaskIds.length > 0
            ? { retained_ids: retainedTaskIds.join(',') }
            : {}),
          ...(ordering ? { ordering } : {}),
        },
        options: { signal },
      }) as Promise<TasksResponse>,
    select: (data): InfiniteData<TasksListResponse> => {
      const seenTaskIds = new Set<number>();

      return {
        ...data,
        pages: (data.pages ?? []).map((page) => ({
          ...page,
          results: page.results.map(taskFromDto).filter((task) => {
            if (seenTaskIds.has(task.id)) {
              return false;
            }
            seenTaskIds.add(task.id);
            return true;
          }),
        })),
      };
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.meta.next) {
        return undefined;
      }
      return lastPage.meta.page + 1;
    },
    structuralSharing: (oldData, newData) =>
      taskListCache.preserveRetainedRows(oldData, newData, preservedTaskIds),
  });
}

export function useTaskTypes(searchQuery = '') {
  return useQuery<TaskTypeChoicesResponseDto, TaskTypeChoice[]>(
    'tasks/types/',
    {
      initialData: { results: [] },
      queryParams: {
        page_size: '500',
        ...(searchQuery ? { q: searchQuery } : {}),
      },
      select: (data) => data.results.map(taskTypeChoiceFromDto),
    },
  );
}

export function useTaskStatusChoices() {
  return useQuery<TaskStatusChoicesResponse>('tasks/choices/task-statuses/', {
    initialData: {},
  });
}

export function useTaskPriorityChoices() {
  return useQuery<TaskPriorityChoicesResponse>(
    'tasks/choices/task-priorities/',
    {
      initialData: {},
    },
  );
}

export function useTaskSummary() {
  return useQuery<TaskSummaryResponse>('tasks/summary/', {
    initialData: {
      overdue_tasks: 0,
      due_tasks: 0,
      pending_tasks: 0,
    },
  });
}

export function useUpdateTask() {
  return useMutation<TaskDto, Error, UpdateTaskPayload>({
    mutationKey: ['root', 'tasks', 'update'],
    mutateFn: ({ apiClient, id, payload }) =>
      apiClient.request({
        url: `${tasksUrl}${id}/`,
        method: 'PATCH',
        payload: taskUpdateToDto(payload) as Partial<TaskUpdateDto>,
      }) as Promise<TaskDto>,
    onSuccess: (updatedTaskDto) => {
      updateTaskInCachedLists(updatedTaskDto);
      void invalidateTaskSummary();
    },
    onError: handleTaskMutationError,
  });
}

export function useCreateTask() {
  return useMutation<TaskDto, Error, TaskCreate>({
    mutationKey: ['root', 'tasks', 'create'],
    mutateFn: ({ apiClient, ...payload }) =>
      apiClient.request({
        url: tasksUrl,
        method: 'POST',
        payload: taskCreateToDto(payload) as TaskCreateDto,
      }) as Promise<TaskDto>,
    onSuccess: () => {
      void invalidateTaskSummary();
    },
    onError: handleTaskMutationError,
  });
}

export function useReassignTask() {
  return useMutation<TaskDto, Error, TaskReassign>({
    mutationKey: ['root', 'tasks', 'reassign'],
    mutateFn: ({ apiClient, id, taskUserId }) =>
      apiClient.request({
        url: `${tasksUrl}${id}/reassign/`,
        method: 'POST',
        payload: {
          task_user: taskUserId,
        } satisfies TaskReassignDto,
      }) as Promise<TaskDto>,
    onSuccess: (updatedTaskDto) => {
      updateTaskInCachedLists(updatedTaskDto);
      void invalidateTaskSummary();
    },
    onError: handleTaskMutationError,
  });
}

export function useCompleteTask() {
  return useMutation<TaskDto, Error, TaskComplete>({
    mutationKey: ['root', 'tasks', 'complete'],
    mutateFn: ({ apiClient, completed, id }) =>
      apiClient.request({
        url: `${tasksUrl}${id}/complete/`,
        method: 'POST',
        payload: {
          task_completed: completed,
        } satisfies TaskCompleteDto,
      }) as Promise<TaskDto>,
    onSuccess: (updatedTaskDto) => {
      updateTaskInCachedLists(updatedTaskDto);
      void invalidateTaskSummary();
    },
    onError: handleTaskMutationError,
  });
}

export function usePrioritizeTask() {
  return useMutation<TaskDto, Error, TaskPrioritize>({
    mutationKey: ['root', 'tasks', 'prioritize'],
    mutateFn: ({ apiClient, id, prioritized }) =>
      apiClient.request({
        url: `${tasksUrl}${id}/prioritize/`,
        method: 'POST',
        payload: {
          task_prioritized: prioritized,
        } satisfies TaskPrioritizeDto,
      }) as Promise<TaskDto>,
    onSuccess: (updatedTaskDto) => {
      updateTaskInCachedLists(updatedTaskDto);
    },
    onError: handleTaskMutationError,
  });
}
